/**
 * Parse spreadsheet paste text (TSV from Excel/Sheets, or CSV) into guest row objects.
 * Header aliases match server PASTE_HEADER_ALIASES (MVP columns).
 */

const HEADER_ALIASES = {
	firstname: 'firstName',
	'first name': 'firstName',
	first_name: 'firstName',
	lastname: 'lastName',
	'last name': 'lastName',
	last_name: 'lastName',
	email: 'email',
	'e-mail': 'email',
	phone: 'phone',
	telephone: 'phone',
	mobile: 'phone',
	role: 'roleInWedding',
	roleinwedding: 'roleInWedding',
	'role in wedding': 'roleInWedding',
	'wedding role': 'roleInWedding',
	guesttags: 'guestTags',
	'guest tags': 'guestTags',
	tags: 'guestTags',
}

const CANONICAL_HEADERS = [
	'firstName',
	'lastName',
	'email',
	'phone',
	'roleInWedding',
	'guestTags',
]

/** Detect delimiter: tab if present in first line, else comma. */
function detectDelimiter(text) {
	const firstLine = text.split(/\r?\n/).find((l) => l.trim()) || ''
	if (firstLine.includes('\t')) return '\t'
	return ','
}

/**
 * Split a CSV/TSV line respecting simple double-quoted fields.
 */
function splitLine(line, delimiter) {
	const cells = []
	let current = ''
	let inQuotes = false

	for (let i = 0; i < line.length; i++) {
		const ch = line[i]
		if (ch === '"') {
			if (inQuotes && line[i + 1] === '"') {
				current += '"'
				i++
			} else {
				inQuotes = !inQuotes
			}
			continue
		}
		if (ch === delimiter && !inQuotes) {
			cells.push(current.trim())
			current = ''
			continue
		}
		current += ch
	}
	cells.push(current.trim())
	return cells
}

function normalizeHeader(raw) {
	const key = String(raw ?? '')
		.trim()
		.toLowerCase()
	return HEADER_ALIASES[key] || null
}

/**
 * @param {string} text - pasted spreadsheet text
 * @returns {{ rows: Record<string, string>[], error?: string }}
 */
export function parseGuestPasteText(text) {
	const trimmed = String(text ?? '').trim()
	if (!trimmed) {
		return { rows: [], error: 'Paste at least one header row and one data row' }
	}

	const delimiter = detectDelimiter(trimmed)
	const lines = trimmed
		.split(/\r?\n/)
		.map((l) => l.trimEnd())
		.filter((l) => l.trim().length > 0)

	if (lines.length < 2) {
		return {
			rows: [],
			error: 'Include a header row and at least one guest row',
		}
	}

	const headerCells = splitLine(lines[0], delimiter)
	const mappedHeaders = headerCells.map(normalizeHeader)

	if (!mappedHeaders.some(Boolean)) {
		return {
			rows: [],
			error:
				'Unrecognized headers. Use: First Name, Last Name, Email, Phone, Role, Guest Tags',
		}
	}

	if (!mappedHeaders.includes('firstName') || !mappedHeaders.includes('lastName')) {
		return {
			rows: [],
			error: 'Headers must include First Name and Last Name',
		}
	}

	const rows = []
	for (let i = 1; i < lines.length; i++) {
		const cells = splitLine(lines[i], delimiter)
		const row = {}
		for (let c = 0; c < mappedHeaders.length; c++) {
			const field = mappedHeaders[c]
			if (!field) continue
			const value = cells[c] ?? ''
			if (value === '') continue
			row[field] = value
		}
		// Skip completely empty data lines
		if (Object.keys(row).length === 0) continue
		rows.push(row)
	}

	if (rows.length === 0) {
		return { rows: [], error: 'No guest rows found under the header' }
	}

	if (rows.length > 500) {
		return { rows: [], error: 'Maximum 500 rows per import' }
	}

	return { rows }
}

export const GUEST_PASTE_COLUMN_HELP = CANONICAL_HEADERS
